import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Plus } from 'lucide-react';

const API = 'https://apissc.sainikschoolcadet.com/api/promotional-banners';
const ALLOWED = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_MB = 5; // keep in sync with BANNER_MAX_UPLOAD_MB on the API
const PLACEMENTS = [
  { value: 'above_shorts', label: 'Above Shorts & Quick Lessons' },
  { value: 'bottom', label: 'Bottom of home page' },
];
const placementLabel = (v) => PLACEMENTS.find((p) => p.value === v)?.label || 'Bottom of home page';

const formatSize = (bytes) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(2)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);
const isHttps = (value) => {
  try { return new URL(value.trim()).protocol === 'https:'; } catch { return false; }
};

const emptyForm = { banner_key: '', placement: '', action_url: '', tag: '', active: true };

const PromotionalBannersSection = ({ showNotification }) => {
  const [banners, setBanners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // { src, name, size, width, height, existing }
  const [errors, setErrors] = useState({});
  const [progress, setProgress] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInput = useRef(null);
  const savingRef = useRef(false);

  const handleApiError = useCallback((error, fallback) => {
    showNotification(error.response?.data?.message || (error.response ? fallback : 'Network error. Please try again.'), 'error');
  }, [showNotification]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setLoadError(false);
    try {
      const res = await axios.get(API);
      setBanners([...res.data].sort((a, b) => b.id - a.id));
    } catch (error) {
      setLoadError(true);
      handleApiError(error, 'Failed to load promotional banners');
    } finally {
      setIsLoading(false);
    }
  }, [handleApiError]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setForm(emptyForm);
    setFile(null);
    setPreview(null);
    setErrors({});
    setProgress(null);
    if (fileInput.current) fileInput.current.value = '';
  };

  const openNew = () => { closeModal(); setModalOpen(true); };

  const openEdit = (b) => {
    closeModal();
    setEditing(b);
    setForm({ banner_key: b.banner_key || '', placement: b.placement || 'bottom', action_url: b.action_url || '', tag: b.tag || '', active: !!b.active });
    setPreview({ src: b.image_path, name: b.original_filename, size: b.size_bytes, width: b.width, height: b.height, existing: true });
    setModalOpen(true);
  };

  const set = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.value }));

  const onFile = (e) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    if (!ALLOWED.includes(picked.type)) {
      setErrors((p) => ({ ...p, image: 'Unsupported file type. Use JPEG, PNG or WebP.' }));
      e.target.value = '';
      return;
    }
    if (picked.size > MAX_MB * 1024 * 1024) {
      setErrors((p) => ({ ...p, image: `File is too large (${formatSize(picked.size)}). Maximum is ${MAX_MB} MB.` }));
      e.target.value = '';
      return;
    }
    setErrors((p) => ({ ...p, image: undefined }));
    setFile(picked);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const src = ev.target.result;
      const img = new Image();
      img.onload = () => setPreview({ src, name: picked.name, size: picked.size, width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => {
        setFile(null);
        setPreview(null);
        setErrors((p) => ({ ...p, image: 'This file could not be read as an image.' }));
      };
      img.src = src;
    };
    reader.readAsDataURL(picked);
  };

  const removeSelected = () => {
    setFile(null);
    setPreview(editing ? { src: editing.image_path, name: editing.original_filename, size: editing.size_bytes, width: editing.width, height: editing.height, existing: true } : null);
    if (fileInput.current) fileInput.current.value = '';
  };

  const validate = () => {
    const next = {};
    if (!/^[a-z0-9][a-z0-9_-]{1,99}$/.test(form.banner_key.trim().toLowerCase())) next.banner_key = 'Required: 2-100 characters, letters, numbers, - or _';
    if (!form.placement) next.placement = 'Select where the banner appears';
    if (!form.action_url.trim()) next.action_url = 'Destination URL is required';
    else if (!isHttps(form.action_url)) next.action_url = 'Destination must be a valid https:// URL';
    if (!editing && !file) next.image = 'Please select the banner image to upload';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (savingRef.current || !validate()) return; // blocks double submits
    savingRef.current = true;
    setSaving(true);
    setProgress(0);
    try {
      const body = new FormData();
      body.append('banner_key', form.banner_key.trim().toLowerCase());
      body.append('placement', form.placement);
      body.append('action_url', form.action_url.trim());
      body.append('tag', form.tag);
      body.append('active', form.active ? 'true' : 'false');
      if (file) {
        body.append('image', file);
        if (preview?.width) { body.append('image_width', preview.width); body.append('image_height', preview.height); }
      }
      const config = { onUploadProgress: (ev) => ev.total && setProgress(Math.round((ev.loaded / ev.total) * 100)) };
      if (editing) await axios.put(`${API}/${editing.id}`, body, config);
      else await axios.post(API, body, config);
      showNotification(form.active ? 'Banner saved and published' : 'Banner saved (not published)');
      closeModal();
      fetchData();
    } catch (error) {
      if (error.response?.data?.errors) setErrors(error.response.data.errors);
      handleApiError(error, 'Failed to save banner');
      setProgress(null);
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const toggle = async (b) => {
    try {
      await axios.post(`${API}/${b.id}/${b.active ? 'unpublish' : 'publish'}`);
      showNotification(b.active ? 'Banner disabled' : 'Banner published');
      fetchData();
    } catch (error) {
      handleApiError(error, 'Failed to update banner status');
    }
  };

  const remove = async (b) => {
    if (!window.confirm('Delete this banner permanently?')) return;
    try {
      await axios.delete(`${API}/${b.id}`);
      showNotification('Banner deleted');
      fetchData();
    } catch (error) {
      handleApiError(error, 'Failed to delete banner');
    }
  };

  const field = (name) => `w-full p-2 border rounded ${errors[name] ? 'border-red-500' : 'border-gray-300'}`;

  return (
    <div className="mb-10">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-medium text-gray-900">Promotional Banners</h2>
        <button type="button" onClick={openNew} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700">
          <Plus size={16} className="mr-1" /> Add New
        </button>
      </div>

      {/* All banners */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 text-left">ID</th><th className="p-2 text-left">Image</th><th className="p-2 text-left">Identifier</th>
              <th className="p-2 text-left">Placement</th><th className="p-2 text-left">Action URL</th><th className="p-2 text-left">Status</th><th className="p-2" />
            </tr>
          </thead>
          <tbody>
            {banners.length === 0 && (
              <tr><td colSpan="7" className="p-4 text-center text-gray-500">{isLoading ? 'Loading…' : loadError ? <>Could not load banners. <button type="button" onClick={fetchData} className="underline">Retry</button></> : 'No promotional banners found'}</td></tr>
            )}
            {banners.map((b) => (
              <tr key={b.id} className="border-t">
                <td className="p-2">{b.id}</td>
                <td className="p-2"><img src={b.image_path} alt="" className="w-24 h-auto border rounded" /></td>
                <td className="p-2 font-mono text-xs">{b.banner_key}</td>
                <td className="p-2">{placementLabel(b.placement)}</td>
                <td className="p-2 break-all max-w-xs">{b.action_url}</td>
                <td className="p-2">{b.active ? 'Published' : 'Disabled'}</td>
                <td className="p-2 whitespace-nowrap space-x-2">
                  <button type="button" onClick={() => openEdit(b)} className="text-blue-700 underline">Edit</button>
                  <button type="button" onClick={() => toggle(b)} className="text-amber-700 underline">{b.active ? 'Disable' : 'Publish'}</button>
                  <button type="button" onClick={() => remove(b)} className="text-red-700 underline">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add / edit modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSubmit} className="space-y-4">
              <h2 className="text-xl font-bold">{editing ? 'Edit Promotional Banner' : 'Add New Promotional Banner'}</h2>

              <div>
                <label htmlFor="banner_key" className="block mb-1 font-medium">Identifier *</label>
                <input id="banner_key" type="text" value={form.banner_key} onChange={set('banner_key')} disabled={saving} placeholder="e.g. sainik-abhyas" className={field('banner_key')} />
                <p className="text-xs text-gray-500 mt-1">Unique name for this banner. Lowercase letters, numbers, - or _.</p>
                {errors.banner_key && <p className="text-red-500 text-sm mt-1">{errors.banner_key}</p>}
              </div>

              <div>
                <label htmlFor="banner_placement" className="block mb-1 font-medium">Placement *</label>
                <select id="banner_placement" value={form.placement} onChange={set('placement')} disabled={saving} className={field('placement')}>
                  <option value="">Select where it appears</option>
                  {PLACEMENTS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                <p className="text-xs text-gray-500 mt-1">Only one banner is live per placement; publishing replaces the current one.</p>
                {errors.placement && <p className="text-red-500 text-sm mt-1">{errors.placement}</p>}
              </div>

              <div>
                <label htmlFor="banner_url" className="block mb-1 font-medium">Action URL *</label>
                <input id="banner_url" type="url" value={form.action_url} onChange={set('action_url')} disabled={saving} placeholder="https://example.com/banner" className={field('action_url')} />
                {errors.action_url && <p className="text-red-500 text-sm mt-1">{errors.action_url}</p>}
              </div>

              <div>
                <label htmlFor="banner_tag" className="block mb-1 font-medium">Tag</label>
                <input id="banner_tag" type="text" value={form.tag} onChange={set('tag')} disabled={saving} placeholder="Internal label (optional)" className={field('tag')} />
              </div>

              <div>
                <label htmlFor="banner_image" className="block mb-1 font-medium">Image {!editing && '*'}</label>
                <input ref={fileInput} id="banner_image" type="file" accept="image/jpeg,image/png,image/webp" onChange={onFile} disabled={saving} className={field('image')} />
                <p className="text-xs text-gray-500 mt-1">JPEG, PNG or WebP, up to {MAX_MB} MB.{editing && ' Leave empty to keep the current image.'}</p>
                {errors.image && <p className="text-red-500 text-sm mt-1">{errors.image}</p>}
              </div>

              {preview && (
                <div className="border rounded p-3 bg-gray-50">
                  <p className="text-sm font-medium mb-2">{preview.existing ? 'Current image' : 'Preview (not yet published)'}</p>
                  <img src={preview.src} alt="Banner preview" className="w-full h-auto border rounded bg-white" />
                  <p className="text-xs text-gray-600 mt-2 break-all">
                    {preview.name && <>{preview.name} · </>}{preview.size ? `${formatSize(preview.size)} · ` : ''}{preview.width ? `${preview.width}×${preview.height}px` : ''}
                  </p>
                  {file && <button type="button" onClick={removeSelected} className="mt-2 text-sm text-blue-700 underline">Remove selected image</button>}
                </div>
              )}

              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} disabled={saving} className="h-5 w-5" />
                <span>Active (publish to the mobile app)</span>
              </label>

              {progress !== null && (
                <div>
                  <div className="h-2 bg-gray-200 rounded"><div className="h-2 bg-blue-600 rounded transition-all" style={{ width: `${progress}%` }} /></div>
                  <p className="text-xs text-gray-600 mt-1">{progress < 100 ? `Uploading… ${progress}%` : 'Processing…'}</p>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-2">
                <button type="button" onClick={closeModal} disabled={saving} className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">Cancel</button>
                <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60">
                  {saving ? 'Saving…' : editing ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromotionalBannersSection;
